import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import { Sidebar, Menubar, Button, Menu } from '../PrimereactComponents';
import { capitalizeFirstLetter } from '../../utils/texts';
import useDimensions from '../../hooks/useDimensions';
import { useTranslation } from 'react-i18next';
import LanguageSelection from '../LanguageSelection';
import { getApiClient, MunicipalityExtended } from '../../api';
import Search from "../Search/Search";
import { useMainNav } from '../../context/MainNavContext';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { buildCouncilPath } from '../../utils/councilParams';
import type { Councils, GeneralData } from '../../api';
import type { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import "./Menu.css";

function NavMenu() {
    const [visible, setVisible] = useState(false);
    const { width } = useDimensions();
    const { t } = useTranslation();
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const rootVersion = bodyType === undefined;
    const [municipality, setMunicipality] = useState<MunicipalityExtended | null>(null);
    const [basicData, setBasicData] = useState<GeneralData | undefined>(undefined);
    const [councils, setCouncils] = useState<Councils[]>([]);
    const location = useLocation();
    const [searchOpen, setSearchOpen] = useState(false);

    const {
        organOptionLabels,
        municipalityNameOptionLabels
    } = useLocalizationLabels();

    const bodyTypeGenitive = bodyType ? organOptionLabels[bodyType]?.label_genitiv ?? bodyType : '';
    const municipalityName = municipality ? municipalityNameOptionLabels[municipality.id]?.label ?? municipalitaId : '';
    const { mainNavVisible, setMainNavVisible, setMainNavItems } = useMainNav();

    const fetchMunicipalityCouncils = async () => {
        const api = getApiClient();
        const [municipalityData, councilsData, generalData] = await Promise.all([
            api.getMunicipalityByMunicipalityId(municipalitaId),
            api.getCouncilsByMunicipalityIdAndBody(municipalitaId, bodyType ?? ''),
            api.getGeneralDataByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType ?? '', parseInt(zastupitelstvoId ?? '0', 10))
        ]);

        setMunicipality(municipalityData);
        setCouncils(councilsData);
        setBasicData(generalData);
    };

    useEffect(() => {
        if (!rootVersion) {
            fetchMunicipalityCouncils();
        }
    }, [location]);

    // NavLink template
    const temp = (item: MenuItem, options: MenuItemOptions) => {
        return (
            <NavLink
                to={item.url ?? '/'}
                className={({ isActive }) =>
                    isActive ? `menu__link-active menu-item ${options.className}` : `${options.className} menu-item`
                }
                onClick={() => setMainNavVisible(false)}
                end
            >
                <span className={`${item.icon}`}></span>&nbsp;
                <span>{item.label}</span>
            </NavLink>
        );
    };

    const navItems = useMemo(() => {
        if (rootVersion) {
            return [
                {
                    label: t('appName'),
                    icon: 'pi pi-home',
                    url: '/',
                    template: temp
                },
                {
                    label: t('datasets', { ns: 'datasets' }),
                    icon: 'pi pi-database',
                    url: '/datasety',
                    template: temp
                },
                {
                    label: t('partners', { ns: 'partners' }),
                    icon: 'pi pi-users',
                    url: '/spoluprace',
                    template: temp
                },
                {
                    label: t('about', { ns: 'about' }),
                    icon: 'pi pi-fw pi-info-circle',
                    url: `/o-projektu`,
                    template: temp
                },
                {
                    label: t('TopLevelAnalyses.analyses', { ns: 'mainHome' }),
                    icon: 'pi pi-fw pi-chart-line',
                    url: "/stats",
                    template: temp
                }
            ];
        }

        const hasMoreBodies = Object.keys(municipality?.organy || {}).length > 1;
        const bodiesMenuItems = hasMoreBodies ? [
            {
                label: capitalizeFirstLetter(municipalityName || municipalitaId),
                icon: 'pi pi-fw pi-check-square',
                url: buildCouncilPath(municipalitaId),
                template: temp
            },
            {
                label: capitalizeFirstLetter(bodyType),
                icon: 'pi pi-fw pi-check-square',
                url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId),
                template: temp
            },
        ] : [
            {
                label: capitalizeFirstLetter(municipalityName || municipalitaId),
                icon: 'pi pi-fw pi-check-square',
                url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId),
                template: temp
            },
        ];

        return [
            {
                label: t('appName'),
                icon: 'pi pi-home',
                url: '/',
                template: temp
            },
            ...bodiesMenuItems,
            {
                label: t('parties', { ns: 'parties' }),
                icon: 'pi pi-fw pi-users',
                url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'subjekty'),
                template: temp
            },
            // if there is a meeting unpack more items
            ...(
                !!basicData?.nejnovejsi_zasedani?.id ? [
                    {
                        label: capitalizeFirstLetter(t('meetings', { ns: 'meetings' })),
                        icon: 'pi pi-fw pi-book',
                        url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'zasedani'),
                        template: temp
                    },
                    {
                        label: t('votings', { ns: 'votings' }),
                        icon: 'pi pi-fw pi-check-square',
                        url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'hlasovani'),
                        template: temp
                    },
                    {
                        label: t('analysesTitle', { ns: 'analyses' }),
                        icon: 'pi pi-fw pi-chart-line',
                        url: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'analyzy', 'dochazka'),
                        template: temp
                    }
                ] : []
            )
        ];
    }, [basicData?.nejnovejsi_zasedani?.id, bodyType, municipalitaId, municipality?.organy, rootVersion, t, zastupitelstvoId]);

    useEffect(() => {
        const mobileItems = navItems
            .filter((item) => item.url !== '/')
            .map((item) => ({ label: item.label, icon: item.icon, url: item.url }));
        setMainNavItems(mobileItems);
    }, [navItems, setMainNavItems]);

    // Menu item on left
    const mobileViewport = width < 768;
    const start = mobileViewport
        ? (
            <NavLink to='/' className='menu__mobile-logo menu-item' end>
                <span className='pi pi-home'></span>&nbsp;
                <span>{t('appName')}</span>
            </NavLink>
        )
        : width > 1200 ? <></> : (
            <button
                onClick={() => setMainNavVisible(!mainNavVisible)}
                type='button'
                className='p-sidebar-close p-sidebar-icon p-link p-link hamburger-icon menu-item'
                aria-label='Open'
            >
                <span className='p-sidebar-close-icon pi pi-bars' aria-hidden='true'></span>
            </button>
        );

    const currentCouncil = !rootVersion && councils
        ? councils.find((item) => item && item?.poradi === parseInt(zastupitelstvoId ?? '0', 10))
        : undefined;

    const end = (
        <>
            {!rootVersion && (
                <>
                    <Search open={searchOpen} setOpen={setSearchOpen} />
                    {!searchOpen && (
                        <Button
                            style={{ backgroundColor: municipality?.barva }}
                            className='mainmenu__button'
                            onClick={() => setVisible(!visible)}
                            disabled={councils.length < 2}
                        >
                            {councils.length && currentCouncil
                                ? t('Menu.termRange', { from: currentCouncil.funkcni_obdobi_od, to: currentCouncil.funkcni_obdobi_do })
                                : t('Menu.selection', { organ: bodyTypeGenitive })}
                        </Button>
                    )}
                </>
            )}
            <LanguageSelection />
        </>
    );

    return (
        <div className='mainmenu'>
            <Menubar model={mobileViewport ? [] : navItems} start={start} end={end} />

            <Sidebar visible={!mobileViewport && mainNavVisible} position='left' onHide={() => setMainNavVisible(false)}>
                <Menu style={{ width: '100%', border: 'none' }} model={navItems} />
            </Sidebar>

            {!rootVersion && councils && (
                <Sidebar visible={visible} position='right' onHide={() => setVisible(false)}>
                    <h2 className='sidemenu__title'>{t('Menu.selection', { organ: bodyTypeGenitive })}</h2>
                    <nav className='sidemenu__nav-flex'>
                        {councils
                            .sort((a, b) => (a.poradi < b.poradi ? 1 : -1))
                            .map((council) => (
                                <NavLink
                                    key={`${council.poradi}`}
                                    to={buildCouncilPath(municipalitaId, bodyType, council.poradi)}
                                    onClick={() => setVisible(false)}
                                >
                                    <span
                                        className={
                                            zastupitelstvoId === council.poradi.toString()
                                                ? 'sidemenu__nav-link-active'
                                                : 'sidemenu__nav-link'
                                        }
                                    >
                                        {t('Menu.officeTerm', { from: council.funkcni_obdobi_od, to: council.funkcni_obdobi_do })}
                                    </span>
                                </NavLink>
                            ))}
                    </nav>
                </Sidebar>
            )}
        </div>
    );
}

export default NavMenu;